import React, { useState, useEffect } from 'react';
import LessonLayout from '../components/LessonLayout';
import { Card, Row, Col, Alert, Button, Form } from 'react-bootstrap';

const Lesson08_Events = () => {
  const [clickCount, setClickCount] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [inputValue, setInputValue] = useState('');
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    console.log('📖 Lesson 08: Event Handling - Loaded');
  }, []);

  const handleClick = (e) => {
    console.log('🖱️ Click Event:', e.type, 'at', e.clientX, e.clientY);
    setClickCount(prev => prev + 1);
  };

  const handleDoubleClick = () => {
    console.log('🖱️ Double Click Event');
    alert('Double clicked!');
  };

  const handleMouseMove = (e) => {
    setMousePosition({ x: e.clientX, y: e.clientY });
  };

  const handleKeyPress = (e) => {
    console.log('⌨️ Key Pressed:', e.key, 'Code:', e.code);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('📤 Form Submit Event Prevented');
    alert('Form submission prevented!');
  };

  const handleMouseEnter = () => {
    console.log('🖱️ Mouse Enter');
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    console.log('🖱️ Mouse Leave');
    setIsHovered(false);
  };

  return (
    <LessonLayout 
      lessonNumber={8}
      title="Event Handling" 
      description="Master various event handling patterns in React"
    >
      <Alert variant="success">
        <strong>What You'll Learn:</strong>
        <ul className="mb-0 mt-2">
          <li>onClick, onChange, onSubmit events</li>
          <li>Mouse events (hover, enter, leave, move)</li>
          <li>Keyboard events</li>
          <li>Event object and preventDefault()</li>
          <li>Synthetic events in React</li>
        </ul>
      </Alert>

      <Row className="mt-4">
        <Col md={6}>
          <Card className="mb-3">
            <Card.Header>Click Events</Card.Header>
            <Card.Body>
              <h3>Clicks: {clickCount}</h3>
              <Button onClick={handleClick} className="me-2">Click Me</Button>
              <Button onClick={handleDoubleClick} variant="secondary">Double Click Me</Button>
            </Card.Body>
          </Card>

          <Card className="mb-3">
            <Card.Header>Mouse Events</Card.Header>
            <Card.Body>
              <div 
                className={`demo-box ${isHovered ? 'active' : ''}`}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                style={{ padding: '40px', textAlign: 'center' }}
              >
                {isHovered ? '🎉 Hovering!' : '👆 Hover over me'}
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="mb-3">
            <Card.Header>Keyboard Events</Card.Header>
            <Card.Body>
              <Form.Control
                type="text"
                placeholder="Type something..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <p className="mt-2">You typed: {inputValue}</p>
            </Card.Body>
          </Card>

          <Card className="mb-3">
            <Card.Header>Mouse Move Event</Card.Header>
            <Card.Body>
              <div 
                className="demo-box"
                onMouseMove={handleMouseMove}
                style={{ padding: '40px', cursor: 'crosshair' }}
              >
                Mouse Position: X: {mousePosition.x}, Y: {mousePosition.y}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="mb-3">
        <Card.Header>Form Submit Event</Card.Header>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Control type="text" placeholder="Enter something" />
            </Form.Group>
            <Button type="submit">Submit (preventDefault)</Button>
          </Form>
        </Card.Body>
      </Card>

      <Alert variant="warning" className="mt-4">
        <strong>💡 Key Concepts:</strong>
        <ul className="mb-0">
          <li>React uses synthetic events for cross-browser compatibility</li>
          <li>Event handlers receive event object with useful properties</li>
          <li>Use <code>preventDefault()</code> to stop default behavior</li>
          <li>Use <code>stopPropagation()</code> to prevent event bubbling</li>
        </ul>
      </Alert>
    </LessonLayout>
  );
};

export default Lesson08_Events;
